import * as React from 'react';
import { createStyles, Theme, makeStyles } from '@material-ui/core';
import { UserInfo } from '../../User/UserTypes';
import UserRow from '../../User/UserRow';
import InteractableSurface from '../../Surface/InteractableSurface';
import { Typography } from '../../typography/Typography';
import { Shimmer } from '@cube3/cubicle/src/core/atoms/Loader/Shimmer';
import { EllipsisMenu } from '@cube3/cubicle/src/core/molecules/EllipsisMenu';
import { MenuEntry } from '@cube3/cubicle/src/core/molecules/ContextMenu/NestedMenu';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      height: theme.sizing[56]
    },
    menu: {
      padding: '0 8px'
    }
  })
);

interface Properties {
  userInfo?: UserInfo;
  isMember?: boolean;
  isAdmin?: boolean;
  isCurrentAccount?: boolean;
  onRoleChange(role: { role: 'Member' | 'Admin' }): void;
  onRemoveMemberClick: () => void;
  canEdit: boolean;
  memberDetailComponent?: JSX.Element;
}

export const ProjectMemberUserDetailUI = (props: Properties) => {
  const classes = useStyles(props);
  const {
    userInfo,
    isAdmin,
    isMember,
    onRoleChange,
    isCurrentAccount,
    onRemoveMemberClick,
    canEdit,
    memberDetailComponent
  } = props;

  const menu = React.useMemo(
    () => [
      {
        entries: [
          {
            title: 'Admin',
            disabled: !canEdit,
            icon: isAdmin ? 'check' : '',
            onClick: () => onRoleChange({ role: 'Admin' })
          },
          {
            title: 'Member',
            disabled: !canEdit,
            icon: isMember ? 'check' : '',
            onClick: () => onRoleChange({ role: 'Member' })
          },
          {
            disabled: isCurrentAccount ? false : !canEdit,
            title: !isCurrentAccount ? 'Remove member' : 'Leave Project',
            onClick: onRemoveMemberClick
          }
        ] as MenuEntry[]
      }
    ],
    [
      canEdit,
      classes,
      isAdmin,
      isCurrentAccount,
      isMember,
      onRemoveMemberClick,
      onRoleChange
    ]
  );

  if (!userInfo && !memberDetailComponent) {
    return <Shimmer weight="medium" height={56} margin="medium" />;
  }

  return (
    <InteractableSurface
      highlightOnHover={canEdit || isCurrentAccount}
      surfaceProps={{ corners: 'rounded' }}
      classes={{ root: classes.root }}
    >
      {memberDetailComponent ? (
        memberDetailComponent
      ) : (
        <UserRow userInfo={userInfo} />
      )}
      {isAdmin && <Typography color="contrast3">Admin</Typography>}
      {canEdit || isCurrentAccount ? (
        <span className={classes.menu}>
          <EllipsisMenu menu={menu} />
        </span>
      ) : (
        <div style={{ width: 52 }}></div> //necessary for layout purposes
      )}
    </InteractableSurface>
  );
};
